/**
 * Hook para buscar pipelines de uma workspace específica
 * Usado no seletor de troca de pipeline do modal de deal
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-02-09
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('WorkspacePipelines');

// Interface da fase do pipeline
export interface PipelinePhase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
  probability_percent: number | null;
}

// Interface do pipeline com fases
export interface WorkspacePipeline {
  id: string;
  name: string;
  sort_order: number;
  phases: PipelinePhase[];
}

// Opções de configuração do hook
export interface UseWorkspacePipelinesOptions {
  enabled?: boolean;
}

/**
 * Hook para buscar pipelines ativos de uma workspace específica
 * Inclui fases ordenadas por sort_order
 */
export function useWorkspacePipelines(
  workspaceId: string | null,
  options?: UseWorkspacePipelinesOptions
) {
  const { currentTenant } = useAuth();

  // Determina se query deve executar
  const isEnabled = (options?.enabled ?? true) && !!workspaceId && !!currentTenant?.id;

  return useQuery({
    queryKey: ['workspace-pipelines', workspaceId],
    queryFn: async () => {
      if (!workspaceId || !currentTenant?.id) return [];

      logger.debug('Buscando pipelines da workspace:', workspaceId);

      // Busca pipelines ativos da workspace com suas fases
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select(`
          id,
          name,
          sort_order,
          crm_pipeline_phases(
            id,
            name,
            color,
            sort_order,
            is_won,
            is_lost,
            probability_percent
          )
        `)
        .eq('workspace_id', workspaceId)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');

      if (error) {
        logger.error('Erro ao buscar pipelines:', error.message);
        throw error;
      }

      // Transforma dados e ordena fases por sort_order
      const pipelines: WorkspacePipeline[] = (data || []).map(p => ({
        id: p.id,
        name: p.name,
        sort_order: p.sort_order,
        phases: (p.crm_pipeline_phases || [])
          .sort((a, b) => a.sort_order - b.sort_order)
          .map(ph => ({
            id: ph.id,
            name: ph.name,
            color: ph.color,
            sort_order: ph.sort_order,
            is_won: ph.is_won,
            is_lost: ph.is_lost,
            probability_percent: ph.probability_percent
          }))
      }));

      logger.debug(`Encontradas ${pipelines.length} pipelines na workspace`);
      return pipelines;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });
}
